import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function Home() {
  const { t } = useTranslation();
  const [hero, setHero] = useState(
    "https://customer-assets-wrfwihn1.emergentagent.net/job_plan-it-draw/artifacts/17v1o3xn_foto%20ufficio.webp"
  );

  useEffect(() => {
    // First try Teatro Dinamico gallery cover (first photo)
    api.get("/services/spazio-dimostrativo/gallery")
      .then((r) => {
        const arr = r.data?.image_urls || [];
        if (arr.length > 0) { setHero(arr[0]); return true; }
        return false;
      })
      .then((used) => {
        if (used) return;
        // Fallback to /content hero_image
        return api.get("/content").then((r) => {
          if (r.data?.hero_image?.image_url) setHero(r.data.hero_image.image_url);
        });
      })
      .catch(() => {});
  }, []);

  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        {/* Title above everything, single line */}
        <h1
          className="font-neris leading-none text-center whitespace-nowrap"
          data-testid="home-hero-title"
        >
          <span
            className="block text-planit-gradient text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
            style={{ letterSpacing: "0.12em" }}
          >
            PLAN IT
          </span>
          <span className="block mt-3 text-stone-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-widest">
            STUDIO
          </span>
        </h1>

        {/* Two equal-height columns: text | photo */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
          <div className="flex flex-col justify-between">
            <div
              className="text-sm text-stone-700 leading-relaxed italic space-y-3"
              data-testid="home-hero-sub"
            >
              <p>
                «È Maya, il velo ingannatore, che avvolge gli occhi dei mortali e fa loro vedere un mondo del quale non può dirsi né che esista, né che non esista; perché ella rassomiglia al sonno, rassomiglia al riflesso del sole sulla sabbia, che il pellegrino da lontano scambia per acqua.»
              </p>
              <p className="not-italic text-xs uppercase tracking-widest text-stone-500">
                Arthur Schopenhauer — Il mondo come volontà e rappresentazione (1819)
              </p>
              <p>
                La tecnologia può essere un insidioso velo di Maya — in grado di alterare la percezione della realtà, anche nel mondo della progettazione e del design.
              </p>
              <p>
                Il loro è, di contro, un sistema di progettazione intuitivo: la riproduzione in scala reale degli ambienti permette una visione immediata, impattante e chiarificatrice a chi vuole arredare casa o spazi commerciali. I geometri Alessio Uliveti e Andrea Conti ci parlano del loro studio e dei vantaggi del loro metodo.
              </p>
              <p className="not-italic text-xs uppercase tracking-widest text-[#D06D4D]">
                — Simone Bandini, Valley Life
              </p>
              <p className="not-italic text-xs text-stone-500 mt-1">
                <a
                  href="https://www.valleylife.it/it/la-rivoluzione-di-plan-it-studio-la-percezione-del-progetto-tra-illusione-e-realta/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-article-link"
                  className="underline underline-offset-4 hover:text-[#D06D4D] transition-colors inline-flex items-center gap-1"
                >
                  Leggi l'articolo su Valley Life <ArrowUpRight size={12} />
                </a>
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/disegna"
                data-testid="home-cta-draw"
                className="inline-flex items-center gap-2 bg-[#D06D4D] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#b95a3d] transition-colors"
              >
                {t("home.ctaDraw")} <ArrowUpRight size={16} />
              </Link>
              <a
                href="https://calendly.com/planit-studio/teatro-dinamico"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-cta-book"
                className="inline-flex items-center gap-2 bg-[#D06D4D] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#b95a3d] transition-colors"
              >
                {t("home.ctaBook")} <ArrowUpRight size={16} />
              </a>
            </div>
          </div>

          <div className="relative w-full h-full min-h-[420px] lg:min-h-full overflow-hidden rounded-sm">
            <img src={hero} alt="Studio" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/10" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
}
